import { useMemo } from 'react'
import { dateFormat, flatten, numberFormat, round } from 'shared/util'
import { colorTable } from 'shared/colors'

import { useUserSession } from 'features/session/context'
import { useSearch } from 'features/search/context'

import { useListArticleByAuthor } from 'interface/article'
import { useListAuthorsAffiliation, useListAuthorsCollaborator } from 'interface/author'

import {
  Stack,
  StackProps,
  Box,
  Typography,
  Chip,
  ListItemButton,
  ListItemAvatar
} from '@mui/material'

import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab'

import { Label as PinIcon } from '@mui/icons-material'

import { Section, SectionBlock } from 'components/controls/section'
import { ValueSpan } from 'components/controls/valueSpan'
import { LimittedList } from 'components/controls/limittedList'
import { LimittedStack } from 'components/controls/limittedStack'
import { Loading } from 'components/controls/loading'
import { ErrorBlock } from 'components/controls/errorInfo'

import { KojinAvatar } from 'features/kojin/components/avatar'
import { formatJournalDate, getJournalDate } from 'features/article/util'

import { AuthorTypeChip } from './components/authorTypeChip'
import { useInterest } from './components/interestListBlock/container'
import { ApproachListBlock } from './components/approachListBlock'
import { useApproach } from './components/approachListBlock/container'
import { InterestListBlock } from './components/interestListBlock'

import { useKojinPreview } from '../../container'

const limit_mesh_count = 20
const limit_departments_count = 20
const limit_collaboraters_count = 5
const limit_journals_count = 5

type AffilationOrigin = {
  firstDate: string
  lastDate: string
  list: string[]
  last: boolean
}

const colorByWords: {
  word: string
  color: string
}[] = []
const calcWordColor = (word: string) => {
  const exist = colorByWords.find((c) => c.word === word)
  if (exist) return exist.color
  const newColor = colorTable[colorByWords.length % colorTable.length]
  colorByWords.push({ word: word, color: newColor })
  return newColor
}

type KojinJournalPageProps = {
  container: ReturnType<typeof useKojinPreview>
  isSmallMedia: boolean
} & StackProps
export const KojinJournalPage = ({
  container: {
    kojin,
    author,
    onOtherKojinOpen,
    onOtherAuthorOpen,
    onArticleOpen
  },
  isSmallMedia,
  ...others
}: KojinJournalPageProps) => {
  const search = useSearch()
  const { isCareNet, isGeneral } = useUserSession()
  const sectionWidth = isSmallMedia ? '5rem' : undefined

  const articleListState = useListArticleByAuthor(search?.searchId, author?.authorId, { updateTime: search?.updateTime })
  const affiliationState = useListAuthorsAffiliation(search?.searchId, author?.authorId, { updateTime: search?.updateTime })
  const collaboratorState = useListAuthorsCollaborator(search?.searchId, author?.authorId, { updateTime: search?.updateTime })

  const {
    articles
   } = useMemo(
    () => {
      const articles = articleListState.data
      if (articles) {
        articles.sort((a, b) => getJournalDate(b).localeCompare(getJournalDate(a)))
      }
      return { articles }
    },
    [articleListState]
  )

  const {
    departments,
    origins,
  } = useMemo(
    () => {
      const departments: {
        department: string
        count: number
      }[] = []
      const origins: AffilationOrigin[] = []
      let last: AffilationOrigin | null = null
      if (affiliationState.data) {
        for (const { affilationType, affilationName, firstDate, lastDate, count } of affiliationState.data.list) {
          if (affilationType === 1) {
            affilationName.split(/[,|・|･]/g).map(department => {
              const exist = departments.find(exist => exist.department === department)
              if (exist) {
                exist.count += count
              } else {
                departments.push({ department, count })
              }
            })
          } else if (affilationType === 0) {
            if (
              !last ||
              last.firstDate !== firstDate ||
              last.lastDate !== lastDate
            ) {
              last = { firstDate, lastDate, list: [], last: false }
              origins.push(last)
            }
            last.list.push(affilationName)
          }
        }
        departments.sort((a, b) => b.count - a.count)
        if (last) last.last = true
      }
      return { departments, origins }
    },
    [affiliationState]
  )

  const collaborators = useMemo(
    () => {
      return collaboratorState.data?.list ?? []
    },
    [collaboratorState]
  )

  const { words, meshs } = useMemo(
    () => {
      const wordMap: { [index: string]: number } = {}
      let wordTotal = 0
      const meshMap: { [index: string]: number } = {}
      let meshTotal = 0
      for (const article of articles ?? []) {
        for (const word of article.words ?? []) {
          wordMap[word] = (wordMap[word] ?? 0) + 1
          wordTotal += 1
        }
        for (const mesh of article.meshs ?? []) {
          if (!mesh.isMajor) continue
          (mesh.descriptionJpn ?? mesh.description).split(/[,|・|･]/g).forEach(meshWord => {
            meshMap[meshWord] = (meshMap[meshWord] ?? 0) + 1
            meshTotal += 1
          })
        }
      }
      const words = Object.entries(wordMap).map(([word, count]) => ({
        word, count, rate: count / wordTotal
      }))
      words.sort((a, b) => b.count - a.count)
      const meshs = Object.entries(meshMap).map(([mesh, count]) => ({
        mesh, count, rate: count / meshTotal
      }))
      meshs.sort((a, b) => b.count - a.count)
      return { words, meshs }
    },
    [articles]
  )
  const interest = useInterest({ searchId: search?.searchId, authorId: author?.authorId })
  const approach = useApproach({ searchId: search?.searchId, authorId: author?.authorId })

  return author ? (
    <Stack
      direction="column"
      sx={{ height: '100%', boxSizing: 'border-box' }}
      {...others}
    >
      {(
        articleListState.loading ||
        affiliationState.loading ||
        collaboratorState.loading ||
        interest.loading || approach.loading
      ) ? (
        <Loading/>
      ) : (
        <>
          <ErrorBlock
            error={flatten([
              articleListState.error,
              affiliationState.error,
              collaboratorState.error,
              interest.error,
              approach.error
            ])}
          />
          <SectionBlock
            label="サマリー"
            isSmallMedia={isSmallMedia}
            labelWidth={sectionWidth}
          >
            <Stack direction="row" flexWrap="wrap">
              {author.counts.single > 0 && (
                <ValueSpan
                  text={numberFormat(author.counts.single)}
                  prefix="単独著者"
                  suffix="件"
                  stackProps={{ paddingRight: 1 }}
                />
              )}
              {author.counts.first > 0 && (
                <ValueSpan
                  text={numberFormat(author.counts.first)}
                  prefix="先頭著者"
                  suffix="件"
                  stackProps={{ paddingRight: 1 }}
                />
              )}
              {author.counts.second > 0 && (
                <ValueSpan
                  text={numberFormat(author.counts.second)}
                  prefix="第二著者"
                  suffix="件"
                  stackProps={{ paddingRight: 1 }}
                />
              )}
              {author.counts.last > 0 && (
                <ValueSpan
                  text={numberFormat(author.counts.last)}
                  prefix="責任著者"
                  suffix="件"
                  stackProps={{ paddingRight: 1 }}
                />
              )}
              <ValueSpan
                text={numberFormat(author.counts.total)}
                prefix="全"
                suffix="件"
                stackProps={{ paddingRight: 1 }}
              />
            </Stack>

            {words.length > 0 && (
              <>
                <Stack direction="row" flexWrap="wrap" useFlexGap>
                  {words.map((word, i) => (
                    <ValueSpan
                      key={`word-sum-${i}-${word}`}
                      pin={<PinIcon style={{ color: calcWordColor(word.word) }} />}
                      prefix={word.word}
                      text={round(word.rate * 100, 1).toString()}
                      suffix="%"
                      prefixProps={{ translate: 'yes', whiteSpace: 'nowrap' }}
                      stackProps={{ marginRight: 2 }}
                    />
                  ))}
                </Stack>

                <Stack direction="row" padding={0.5}>
                  {words.map((word, i) => (
                    <Box
                      key={`word-bars-${i}-${word}`}
                      style={{
                        width: (word.rate * 100).toString() + '%',
                        height: '1rem',
                        borderTopLeftRadius: i === 0 ? '0.2rem' : 0,
                        borderBottomLeftRadius: i === 0 ? '0.2rem' : 0,
                        borderTopRightRadius: i === (words.length - 1) ? '0.2rem' : 0,
                        borderBottomRightRadius: i === (words.length - 1) ? '0.2rem' : 0,
                        background: calcWordColor(word.word),
                      }}
                    />
                  ))}
                </Stack>
              </>
            )}
          </SectionBlock>

          {isCareNet && isGeneral && (
            <Section label={'専門分野・\n関心領域'} disablePadding>
              <InterestListBlock articles={articles} onArticleOpen={onArticleOpen} {...interest}/>
            </Section>
          )}
          {isCareNet && isGeneral && (
            <Section label={'講演・取材\nテーマ案'} disablePadding>
              <ApproachListBlock articles={articles} onArticleOpen={onArticleOpen} {...approach}/>
            </Section>
          )}

          {(meshs.length > 0) && (
            <SectionBlock
              label="MESH"
              isSmallMedia={isSmallMedia}
              labelWidth={sectionWidth}
            >
              <LimittedStack
                keyPrefix="mesh"
                direction="row"
                flexWrap="wrap"
                list={meshs}
                count={limit_mesh_count}
                record={({ mesh }, i, arr) =>
                  <Typography
                    key={`mesh-${i}`}
                    variant="body2"
                    component="div"
                    translate="yes"
                  >
                    {mesh}
                    {(i < (arr.length - 1)) ? "、" : ""}
                  </Typography>
                }
              />
            </SectionBlock>
          )}

          {(origins.length > 0 || departments.length > 0) && (
            <SectionBlock
              label="所属推移"
              isSmallMedia={isSmallMedia}
              labelWidth={sectionWidth}
            >
              <LimittedStack
                keyPrefix="department"
                direction="row"
                flexWrap="wrap"
                useFlexGap
                list={departments}
                count={limit_departments_count}
                record={({ department }, i, arr) => (
                  <Typography
                    key={`department-${i}`}
                    variant="body2"
                    component="span"
                    translate="yes"
                  >
                    {department}
                    {(i < (arr.length - 1)) ? "、" : ""}
                  </Typography>
                )}
              />
              <Timeline sx={{ paddingX: 0, paddingY: 0.5, margin: 0 }}>
                {origins.map((origin, i) => (
                  <TimelineItem key={`organ-${i}`} sx={{ minHeight: 0 }}>
                    {origin.firstDate ? (
                      origin.firstDate === origin.lastDate ? (
                        <TimelineOppositeContent
                          color="text.secondary"
                          maxWidth={isSmallMedia ? "8em" : "12em" }
                          sx={{ paddingY: 0.5, paddingLeft: 0 }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="div"
                          >
                            {dateFormat(origin.firstDate, { wareki: isSmallMedia ? false : true })}
                          </Typography>
                        </TimelineOppositeContent>
                      ) : (
                        <TimelineOppositeContent
                          color="text.secondary"
                          maxWidth={isSmallMedia ? "8em" : "12em" }
                          sx={{ paddingY: 0.5, paddingLeft: 0 }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="div"
                          >
                            {dateFormat(origin.firstDate, { wareki: isSmallMedia ? false : true })}～
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="div"
                          >
                            {dateFormat(origin.lastDate, { wareki: isSmallMedia ? false : true })}{' '}
                          </Typography>
                        </TimelineOppositeContent>
                      )
                    ) : (
                      <></>
                    )}
                    <TimelineSeparator>
                      <TimelineDot sx={{ marginY: 1 }} />
                      {origin.last ? <></> : <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent sx={{ paddingY: 0.5 }}>
                      {origin.list.map((name, j) => (
                        <Typography
                          key={`organ-${i}-${j}`}
                          variant="body2"
                          color="text.primary"
                          component="div"
                          translate="yes"
                        >
                          {name}
                        </Typography>
                      ))}
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </SectionBlock>
          )}

          {(collaborators.length > 0) && (
            <SectionBlock
              isSmallMedia={isSmallMedia}
              label="共著医師"
              labelWidth={sectionWidth}
              disablePadding
            >
              <LimittedList
                keyPrefix="collabo"
                disablePadding
                list={collaborators}
                count={limit_collaboraters_count}
                record={(collabo, i) =>
                  collabo.kojinCode ? (
                    <ListItemButton
                      onClick={() => onOtherKojinOpen(collabo.kojinCode ?? '')}
                      sx={{ padding: 1 }}
                      alignItems="flex-start"
                    >
                      <ListItemAvatar
                        sx={{
                          minWidth: '42px',
                          marginY: 0.5,
                          textAlign: 'center'
                        }}
                      >
                        <KojinAvatar
                          kojin={collabo.kojin}
                          rank={i + 1}
                          globalRank={collabo.global.rank}
                        />
                      </ListItemAvatar>

                      <Stack direction="column" marginLeft={1} flexGrow={1}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Typography variant="body2" color="text.primary">
                            {collabo.kojin?.isiName}
                          </Typography>
                          {collabo.kojin?.nenrei && (
                            <Typography variant="caption" color="text.secondary">
                              {collabo.kojin.nenrei}歳
                            </Typography>
                          )}
                          {collabo.kojin?.seibetuCode && (
                            <Typography variant="caption" color="text.secondary">
                              {collabo.kojin.seibetuCode === '1' ? '男性' :
                              collabo.kojin.seibetuCode === '2' ? '女性' : 'その他／不明'}
                            </Typography>
                          )}
                          {collabo.kojin?.sotukouName && (
                            <Typography
                              variant="caption"
                              color={kojin?.sotukouCode === collabo.kojin.sotukouCode ? 'text.primary' : 'text.secondary'}
                            >
                              {collabo.kojin.sotukouName}
                            </Typography>
                          )}
                          <Box flexGrow={1} />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            whiteSpace="nowrap"
                          >
                            {collabo.collabo.score} pt.
                          </Typography>
                        </Stack>

                        {collabo.kojin?.organ && collabo.kojin.organ.length > 0 ? (
                          <Stack direction='column' borderTop='1px solid #dcdcdc'>
                            {collabo.kojin.organ.map((organ, j) => (
                              <Stack
                                key={`collabo-${collabo.kojinCode}-organ-${j}`}
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                flexWrap="wrap"
                              >
                                <Typography
                                  variant="caption"
                                  color={kojin?.organ?.some((o) => o.sisetuCode === organ.sisetuCode) ? 'text.primary' : 'text.secondary'}
                                >
                                  {organ.sisetuName}
                                </Typography>
                                {organ.bukaName && (
                                  <Typography variant="caption" color="text.secondary">
                                    {organ.bukaName}
                                  </Typography>
                                )}
                                {organ.yakusyokuName && (
                                  <Typography variant="caption" color="text.secondary">
                                    {organ.yakusyokuName}
                                  </Typography>
                                )}
                              </Stack>
                            ))}
                          </Stack>
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            borderTop='1px solid #dcdcdc'
                          >
                            所属情報なし
                          </Typography>
                        )}
                      </Stack>
                    </ListItemButton>
                  ) : (
                    <ListItemButton
                      key={`collabo-${collabo.authorId}-btn`}
                      onClick={() => onOtherAuthorOpen(collabo.authorId)}
                      sx={{ padding: 1 }}
                    >
                      <ListItemAvatar
                        sx={{ minWidth: '42px', marginY: 0.5, textAlign: 'center' }}
                      >
                        <KojinAvatar
                          kojin={collabo.kojin}
                          rank={i + 1}
                          globalRank={collabo.global.rank}
                        />
                      </ListItemAvatar>

                      <Stack
                        direction="row"
                        width="100%"
                        spacing={1}
                        marginLeft={1}
                        alignItems="center"
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontStyle="italic"
                          flexGrow={1}
                        >
                          {collabo.authorName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          whiteSpace="nowrap"
                        >
                          {collabo.collabo.score} pt.
                        </Typography>
                      </Stack>
                    </ListItemButton>
                  )
                }
              />
            </SectionBlock>
          )}

          {(articles && articles.length > 0) && (
            <SectionBlock
              label="論文"
              isSmallMedia={isSmallMedia}
              labelWidth={sectionWidth}
              disablePadding
            >
              <LimittedList
                keyPrefix="article"
                list={articles}
                count={limit_journals_count}
                record={(article, i) => (
                  <ListItemButton
                    onClick={() => onArticleOpen(article, articles ?? [])}
                    sx={{ padding: 1 }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      flexGrow={1}
                    >
                      <Chip
                        label={`${i + 1}`}
                        variant="outlined"
                        size="small"
                        sx={{
                          borderRadius: 1,
                          height: 'inherit',
                          minWidth: '2.5em',
                          padding: 0,
                          flexShrink: 0,
                        }}
                      />
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                        justifyContent="flex-end"
                        flexGrow={1}
                        flexWrap="wrap"
                      >
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.primary"
                          flexGrow={1}
                          translate="yes"
                        >
                          {article.titleJpn ?? article.title ?? 'No title'}
                        </Typography>
                        {/*(article.words && article.words.length > 0) && (
                          <Typography
                            variant='caption'
                            color='text.primary'
                            component='div'
                            whiteSpace='nowrap'
                            translate='yes'
                          >{article.words.join(', ')}</Typography>
                        )*/}
                        {article.referenceCount > 0 && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.primary"
                            whiteSpace="nowrap"
                          >
                            {numberFormat(article.referenceCount)}件の参照
                          </Typography>
                        )}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          whiteSpace="nowrap"
                        >
                          {formatJournalDate(article)}
                        </Typography>

                        <AuthorTypeChip authorType={article.authorType} sx={{ height: 'inherit', padding: 0 }}/>
                      </Stack>
                    </Stack>
                  </ListItemButton>
                )}
              />
            </SectionBlock>
          )}
        </>
      )}
      {isSmallMedia === false && <Section labelWidth={sectionWidth} flexGrow={1} />}
    </Stack>
  ) : <></>
}
